/**
 * selectReportPanels — the single source of truth for which report panels mount
 * under a given estate state. Pure (no JSX, no React) so it can be unit-tested in
 * the existing node:test suite, while the view layer stays a thin
 * `{panels.showX && <X/>}` wiring over it.
 *
 * WHY THIS EXISTS: `tsc` proves the panels receive correctly-typed props; it does
 * NOT prove the conditional was written. Centralising the branch here makes the
 * decision testable without a DOM, and leaves the components with one trivial,
 * eyeball-verifiable line each.
 *
 * The load-bearing rule is figure suppression on out-of-range death dates, keyed
 * to WHICH unsupported regime applies (see showFigures below).
 */

import type { IHTCalculation } from "../../types";

/** Pre-1986: Capital Transfer Tax / Estate Duty — the tool cannot compute it. */
export const BLOCKER_PRE_IHT = "PRE_IHT_DEATH_DATE";
/** 1986–2017: computable but missing modern structure (RNRB etc.). */
export const BLOCKER_PRE_2017 = "UNSUPPORTED_DEATH_DATE_PRE_2017";

export type ReportBannerKind = "pre_iht" | "pre_2017";

export interface ReportPanels {
  /** "Date of death outside supported range" warning. */
  showBanner: boolean;
  /** Drives the banner copy; null when no banner. */
  bannerKind: ReportBannerKind | null;
  /** The tax figures / breakdown panel. */
  showFigures: boolean;
  /** RnrbExplanation — consumed by the results/report composition. */
  showRnrb: boolean;
  /** GiftTaperBreakdown — consumed by the results/report composition. */
  showTaper: boolean;
}

const NONE: ReportPanels = {
  showBanner: false,
  bannerKind: null,
  showFigures: false,
  showRnrb: false,
  showTaper: false,
};

export function selectReportPanels(calc: IHTCalculation | null | undefined): ReportPanels {
  if (!calc) return NONE;

  const blockers = calc.blockers ?? [];
  const isPreIht = blockers.includes(BLOCKER_PRE_IHT);
  const isPre2017 = blockers.includes(BLOCKER_PRE_2017);

  // Banner + its copy are driven by the blocker (the authoritative date-gate
  // signal). pre-IHT takes precedence if both were somehow present.
  const bannerKind: ReportBannerKind | null = isPreIht ? "pre_iht" : isPre2017 ? "pre_2017" : null;
  const showBanner = bannerKind !== null;

  // FIGURE SUPPRESSION — the safety property:
  //   • pre-IHT  → SUPPRESS. Any IHT figure would be computed under bands that
  //     did not exist for that death; it would be a confident wrong number.
  //   • pre-2017 → SHOW (indicative). Computable but incomplete, so the figures
  //     render *alongside* the banner rather than being hidden.
  //   • supported → SHOW.
  const showFigures = !isPreIht;

  // RNRB did not exist before 6 Apr 2017, so it never shows for an unsupported
  // date; otherwise it shows when there is an RNRB amount to explain.
  const rnrbAvailable = calc.breakdown?.rnrb_available ?? 0;
  const showRnrb = showFigures && !showBanner && rnrbAvailable > 0;

  // Taper breakdown shows only when figures show and there is an in-window
  // failed PET (the engine emits gift_taper_details for exactly those).
  const hasTaper = (calc.breakdown?.gift_taper_details?.length ?? 0) > 0;
  const showTaper = showFigures && hasTaper;

  return { showBanner, bannerKind, showFigures, showRnrb, showTaper };
}
